import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from './api';

interface AuthUser {
  id: number;
  email: string;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  role: string;
  // null for individual (non-company) accounts — see isIndividual()
  company_id: number | null;
  account_type?: string | null;
  company_name?: string;
  avatar_url?: string | null;
  company_logo_url?: string | null;
  can_post_as_company?: boolean | number;
  headline?: string | null;
  bio?: string | null;
}

// Individual (non-company) account: no company_id (the backend resolves the reserved
// system company to null) or an explicit account_type. Company members have a company_id.
export function isIndividual(u: AuthUser | null | undefined): boolean {
  if (!u) return false;
  return u.account_type === 'individual' || u.company_id == null;
}

export function displayName(u: AuthUser | null | undefined): string {
  if (!u) return '';
  const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return full || u.name || u.email || '';
}

export function initials(u: AuthUser | null | undefined): string {
  if (!u) return '?';
  const fn = u.first_name?.[0] || '';
  const ln = u.last_name?.[0] || '';
  if (fn || ln) return (fn + ln).toUpperCase();
  return (u.email?.[0] || '?').toUpperCase();
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => void;
  applySession: (token: string, user: AuthUser) => void;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  login: async () => ({} as AuthUser),
  logout: () => {},
  applySession: () => {},
  refreshUser: async () => {},
  isAuthenticated: false,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('mt_token');
    if (token) {
      api.getMe()
        .then(({ user }) => setUser(user))
        .catch(() => localStorage.removeItem('mt_token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { token, user } = await api.login(email, password);
    localStorage.setItem('mt_token', token);
    setUser(user);
    return user as AuthUser;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('mt_token');
    setUser(null);
  }, []);

  const applySession = useCallback((token: string, nextUser: AuthUser) => {
    localStorage.setItem('mt_token', token);
    setUser(nextUser);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { user: u } = await api.getMe();
      setUser(u);
    } catch { /* ignore */ }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, applySession, refreshUser, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
